import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import {
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/Components/ui/dialog';
import { Input } from '@/Components/ui/input';
import { ScrollArea } from '@/Components/ui/scroll-area';
import { router } from '@inertiajs/react';
import * as Icons from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface SearchSuggestion {
    text: string;
    icon?: keyof typeof Icons;
    type?: 'category' | 'popular';
}

interface SearchDialogProps {
    onOpenChange: (open: boolean) => void;
    suggestions?: {
        categories?: SearchSuggestion[];
        popular?: SearchSuggestion[];
    };
}

const SearchDialog: React.FC<SearchDialogProps> = ({
    onOpenChange,
    suggestions: backendSuggestions,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);

    const suggestions: SearchSuggestion[] = [
        ...(backendSuggestions?.categories || []),
        ...(backendSuggestions?.popular || []),
    ];

    useEffect(() => {
        // Load recent searches from localStorage
        const saved = localStorage.getItem('recentSearches');
        if (saved) {
            setRecentSearches(JSON.parse(saved));
        }
    }, []);

    const saveRecentSearch = (query: string) => {
        const updated = [
            query,
            ...recentSearches.filter((item) => item !== query),
        ].slice(0, 5);
        setRecentSearches(updated);
        localStorage.setItem('recentSearches', JSON.stringify(updated));
    };

    const handleSearch = (query: string = searchQuery) => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery) return;

        setIsSearching(true);
        saveRecentSearch(trimmedQuery);

        router.visit(`/search?query=${encodeURIComponent(trimmedQuery)}`, {
            preserveState: true,
            preserveScroll: true,
            onFinish: () => {
                setIsSearching(false);
                onOpenChange(false);
            },
        });
    };

    const renderIcon = (iconName: string) => {
        const IconComponent = Icons[
            iconName as keyof typeof Icons
        ] as React.FC<{ className?: string }>;
        return IconComponent ? (
            <IconComponent className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        ) : null;
    };

    return (
        <DialogContent className="sm:max-w-xl">
            <DialogHeader>
                <DialogTitle className="text-emerald-900 dark:text-emerald-100">
                    Search Agricultural Solutions
                </DialogTitle>
                <DialogDescription className="text-emerald-700 dark:text-emerald-300">
                    Discover sustainable farming practices and resources
                </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
                {/* Search Input */}
                <div className="flex gap-2">
                    <Input
                        placeholder="Search for farming techniques, resources..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border-emerald-200 bg-white/50 placeholder:text-emerald-400 focus:border-emerald-500 dark:border-emerald-800 dark:bg-gray-900/50 dark:placeholder:text-emerald-600"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                    <Button
                        onClick={() => handleSearch()}
                        disabled={isSearching}
                        className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
                    >
                        {isSearching ? (
                            <Icons.Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Icons.Search className="h-4 w-4" />
                        )}
                    </Button>
                </div>

                <ScrollArea className="max-h-[60vh]">
                    {/* Recent Searches */}
                    {recentSearches.length > 0 && (
                        <div className="mb-6">
                            <h3 className="mb-2 text-sm font-medium text-emerald-800 dark:text-emerald-200">
                                Recent Searches
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {recentSearches.map((search, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSearch(search)}
                                        className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-sm text-emerald-700 transition-colors hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-800/50"
                                    >
                                        <Icons.Clock className="h-3 w-3" />
                                        {search}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Suggestions */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                            Popular Categories
                        </h3>
                        <div className="grid gap-2 sm:grid-cols-2">
                            {suggestions.map((suggestion, index) => (
                                <button
                                    key={index}
                                    onClick={() =>
                                        handleSearch(suggestion.text)
                                    }
                                    className="flex items-center gap-3 rounded-lg border border-emerald-100 bg-white/50 p-3 text-left transition-all hover:border-emerald-200 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-amber-50 hover:shadow-sm dark:border-emerald-800/30 dark:bg-gray-800/50 dark:hover:border-emerald-700/30 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30"
                                >
                                    {suggestion.icon && (
                                        <div className="rounded-md bg-emerald-100 p-2 dark:bg-emerald-900/50">
                                            {renderIcon(suggestion.icon)}
                                        </div>
                                    )}
                                    <span className="text-sm text-emerald-900 dark:text-emerald-100">
                                        {suggestion.text}
                                    </span>
                                    {suggestion.type === 'popular' && (
                                        <Badge
                                            variant="secondary"
                                            className="ml-auto bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"
                                        >
                                            Popular
                                        </Badge>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </ScrollArea>
            </div>
        </DialogContent>
    );
};

export default SearchDialog;
